import { RefreshCw, Clock } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";
import { useApi } from "@/hooks/useApi";
import { getBotStatus, splitStatus, isOkValue } from "@/api/bot";
import { PageHeader } from "@/components/PageHeader";
import { StatusCard } from "@/components/StatusCard";
import { LoadingState, ErrorState, OfflineState } from "@/components/States";
import { GlassCard } from "@/components/GlassCard";

const SERVICE_LABELS = {
  bot: "Xevon Bot",
  api: "API",
  database: "Database",
  redis: "Redis",
};

export default function Status() {
  useSEO({
    title: "Status",
    description: "Live status of Xevon services — bot, API, database, and Redis, straight from the production API.",
    path: "/status",
  });

  const { data, loading, error, lastChecked, refetch } = useApi(getBotStatus, { autoRefresh: 60000 });

  const { overall, services, latency, guilds, shards } = splitStatus(data);
  const allOk = data ? overall : false;

  return (
    <>
      <PageHeader
        kicker="Live Status"
        title="Is Xevon online right now?"
        description="Real service health, retrieved live from the production Xevon API. No cached dashboards, no stale numbers."
      />
      <section className="mx-auto w-full max-w-5xl px-6 pb-32" data-testid="status-page">
        {loading && <LoadingState label="Loading service status" lines={4} />}

        {!loading && error && (error.kind === "offline" || error.kind === "timeout") && (
          <OfflineState onRetry={refetch} />
        )}
        {!loading && error && error.kind !== "offline" && error.kind !== "timeout" && (
          <ErrorState onRetry={refetch} />
        )}

        {!loading && data && (
          <>
            <GlassCard
              className="mb-8 flex flex-col gap-4 p-6 sm:flex-row sm:items-center sm:justify-between"
              hover={false}
              data-testid="status-summary"
            >
              <div className="flex items-center gap-4">
                <span className="relative flex h-3.5 w-3.5">
                  {allOk && <span className="animate-ping-slow absolute inline-flex h-full w-full rounded-full bg-white" />}
                  <span
                    className={`relative inline-flex h-3.5 w-3.5 rounded-full border ${
                      allOk ? "border-white bg-white" : "border-white/40 bg-transparent"
                    }`}
                  />
                </span>
                <div>
                  <p className="font-display text-lg font-semibold text-paper-100" data-testid="status-overall">
                    {allOk ? "All systems operational" : "Partial service disruption"}
                  </p>
                  {latency !== null && (
                    <p className="font-mono text-xs text-paper-500" data-testid="status-latency">
                      API latency {latency}ms
                      {guilds !== null && ` · ${guilds.toLocaleString("en-US")} servers`}
                      {shards !== null && ` · ${shards} shard${shards === 1 ? "" : "s"}`}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
                {lastChecked && (
                  <span className="inline-flex items-center gap-1.5 text-xs text-paper-500" data-testid="status-last-checked">
                    <Clock className="h-3.5 w-3.5" aria-hidden="true" />
                    Last checked {lastChecked.toLocaleTimeString()}
                  </span>
                )}
                <button
                  onClick={() => refetch()}
                  data-testid="refresh-status-button"
                  className="inline-flex items-center gap-2 rounded-full border border-white/20 px-5 py-2 text-sm font-semibold text-paper-100 transition-colors duration-300 hover:bg-white hover:text-black"
                >
                  <RefreshCw className="h-4 w-4" aria-hidden="true" />
                  Refresh
                </button>
              </div>
            </GlassCard>

            <div className="grid gap-4 sm:grid-cols-2">
              {services.map(([key, value]) => (
                <StatusCard
                  key={key}
                  name={SERVICE_LABELS[key] || key}
                  ok={isOkValue(value)}
                  testId={`status-card-${key}`}
                />
              ))}
            </div>

            <p className="mt-8 text-center font-mono text-xs text-paper-500">
              Auto-refreshes every 60 seconds · Source: /api/v1/bot/status
            </p>
          </>
        )}
      </section>
    </>
  );
}
